import React, { useEffect, useState } from "react";
import { Box, Typography, IconButton, Button } from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CaseDetail from "../../interfaces/caseDetail";
import CollapsibleComponent from "./collapsibleComponents";
import FieldItem from "./FieldItem";
import EditIcon from '@mui/icons-material/Edit';
import apiClient from "../../axiosClient";
import ActionConfirmationModal from "../modals/actionConfirmationModal";
import { CustomerDetail, CustomerDetailResponse, CustomerDetailUpdateRequest } from "../../interfaces/customerDetail";
import { formatNumberToIndianNotation } from "../../utils/commonUtils";
import { AMOUNT } from "../../constants/constants";

interface AdditionalCaseDetailsDataProps {
    caseDetailData: CaseDetail[];
    customerDetailData: CustomerDetailResponse[];
}

const AdditionalCaseDetailsData: React.FC<AdditionalCaseDetailsDataProps> = ({
    caseDetailData,
    customerDetailData,
}) => {
    const [locationPayload, setLocationPayload] = useState<CustomerDetailUpdateRequest>();
    const [modalVisible, setModalVisible] = useState<boolean>(false);
    const openModal = () => setModalVisible(true);
    const closeModal = () => setModalVisible(false);
    const [latlongList, setLatlongList] = useState<CustomerDetailResponse[]>([]);
    const [sortedCustomerDetailData, setSortedCustomerDetailData] = useState<CustomerDetailResponse[]>([]);



    const handlePinnedLocationPress = (lat: string, long: string) => {
        const googleMapsUrl = `https://www.google.com/maps?q=${lat},${long}`;
        window.open(googleMapsUrl, "_blank"); // Open in a new tab
    };

    const setLocationIncorrect = async () => {
        try {
            const response = await apiClient.put('/api/v1/customer_detail', locationPayload);
            if (response.status === 200) {
                window.alert("Success" + response.data.message);
            } else {
                window.alert("Error: " + response.data.message);
            }
        } catch (error) {
            window.alert(`Error while setting location: ${error}`);
        } finally {
            closeModal();
        }
    };

    const handleLocationStatusUpdate = (customerDetail: CustomerDetail) => {
        let customer_detail_vec: CustomerDetailUpdateRequest = {
            customer_detail_id: customerDetail.customer_detail_id,
            tag: "",
            score: 0
        };
        setLocationPayload(customer_detail_vec);
        openModal();
    };

    useEffect(() => {
        if (customerDetailData?.length) {
            const sorted = [...customerDetailData].sort((a, b) => {
                const scoreA = a.customer_detail?.score ?? 0;
                const scoreB = b.customer_detail?.score ?? 0;

                if (scoreA !== scoreB) {
                    return scoreB - scoreA; // DESC
                }

                const dateA = new Date(a.customer_detail?.updated_date || 0);
                const dateB = new Date(b.customer_detail?.updated_date || 0);

                return dateA.getTime() - dateB.getTime(); // ASC
            });

            setSortedCustomerDetailData(sorted);

            const latlongFiltered = sorted.filter(
                (detail: CustomerDetailResponse) => detail.field.field_value_type === "lat_long"
            );

            setLatlongList(latlongFiltered);

        }
    }, [customerDetailData]);

    const getUniqueAddresses = (data: CustomerDetailResponse[]): CustomerDetailResponse[] => {
        const seen = new Set<string>();
        return data.filter(item => {
            const isAddress = item.customer_detail.group_id === "customer" &&
                              item.field.field_value_type === "Address" &&
                              item.customer_detail.field_value;
            if (!isAddress) return false;

            const value = item.customer_detail.field_value.trim();
            if (seen.has(value)) {
                return false;
            }
            seen.add(value);
            return true;
        });
    };

    const uniqueAddresses = getUniqueAddresses(sortedCustomerDetailData);

    return (
        <>
            {modalVisible && (
                <ActionConfirmationModal
                    visible={modalVisible}
                    onClose={closeModal}
                    onConfirm={setLocationIncorrect}
                    headerText="Set Location Incorrect"
                    bodyText="Are you sure you want to flag this location as incorrect?"
                />
            )}
            {uniqueAddresses.length > 0 &&
                <CollapsibleComponent
                    headerText="Address"
                    content={
                        <Box>
                            {uniqueAddresses.map((item: CustomerDetailResponse, index: number) => (
                                <Box key={index}>
                                    <FieldItem
                                        fieldName={item.field.field_name}
                                        value={item.customer_detail.field_value}
                                        fieldType={item.field.field_value_type}
                                        container="Additional Data"
                                    />
                                </Box>
                            ))}
                        </Box>
                    }
                />
            }
            {latlongList?.length > 0 && (
                <CollapsibleComponent
                    headerText="Pinned Location"
                    content={
                        <>
                            {latlongList
                                .map((item, index) => {
                                    let name: string = `Location ${index + 1}`, date: string = '', active: boolean = true;
                                    const [lat, long] = item.customer_detail.field_value?.split(',') || [];
                                    if (item.customer_detail.tag !== "") {
                                        name = item.customer_detail.tag;
                                    }
                                    date = item.customer_detail.updated_date?.substring(0, 10);
                                    if (item.customer_detail.score === 0) {
                                        active = false;
                                    }
                                    return (
                                        <Box key={index} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                                            <div onClick={() => handlePinnedLocationPress(lat, long)}>
                                                <IconButton color="primary" size="small">
                                                    <LocationOnIcon />
                                                    <Typography sx={{ ml: 1 }}>{name}</Typography>
                                                    <Typography sx={{ ml: 1 }}>{date ? `(${date})` : ''}</Typography>
                                                </IconButton>
                                            </div>
                                            {active ? (
                                                <Button color="error" sx={{ ml: 1 }} onClick={() => handleLocationStatusUpdate(item.customer_detail)}>
                                                    <EditIcon />
                                                </Button>
                                            ) : (
                                                <Typography sx={{ ml: 1, color: 'red' }}>Possibly Inaccurate</Typography>
                                            )}
                                        </Box>
                                    );
                                })}
                        </>
                    }
                />
            )}
            {caseDetailData?.length !== 0 &&
                <CollapsibleComponent
                    headerText="Additional Data"
                    content={
                        <Box>
                            {caseDetailData.map((item: CaseDetail, index: number) => item.field_value ? (
                                <Box key={index}>
                                    <FieldItem
                                        fieldName={item.field_name}
                                        value={item.field_value}
                                        fieldType={item.field_value_type} 
                                        container="Additional Data"
                                    />
                                </Box>
                            ) : null)}
                        </Box>
                    }
                />
            }

        </>
    );
};

export default AdditionalCaseDetailsData;
